/**
 * @override
 * King Living QuantityPicker Component
 *
 * Enhanced QuantityPicker with custom styling to match design mockup:
 * - Enabled state: white background, gray border, brown plus button
 * - Disabled state: light gray background, muted colors
 * - Rounded container with minus, input, and plus buttons
 */

import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Button, HStack, Input, useMultiStyleConfig, useNumberInput} from '@salesforce/retail-react-app/app/components/shared/ui';
import BaseQuantityPicker from '@salesforce/retail-react-app/app/components/quantity-picker';
import {
  CartFlyoutQtyMinusIcon,
  CartFlyoutQtyPlusIcon,
} from '@amblique/king-living/app/components/cart-flyout/cart-flyout-qty-icons';
import {
  CheckoutQtyMinusIcon,
  CheckoutQtyPlusIcon,
} from '@amblique/king-living/app/components/order-summary/checkout-qty-icons';

/**
 * @override Enhanced QuantityPicker with custom styling
 */
const QuantityPicker = (props) => {
  const intl = useIntl();
  const {
    isDisabled: propIsDisabled = false,
    disabled: propDisabled = false,
    productName,
    variant = 'default',
    size: sizeProp = 'md',
  } = props;

  const isDisabled = propIsDisabled || propDisabled;
  const isCartFlyout = variant === 'cartFlyout';
  const isCheckout = variant === 'checkout';
  const usesStepperIcons = isCartFlyout || isCheckout;
  const size = isCartFlyout ? 'cartFlyout' : isCheckout ? 'checkout' : sizeProp;
  const buttonVariant = usesStepperIcons ? 'unstyled' : 'outline';
  const styles = useMultiStyleConfig('QuantityPicker', {
    isDisabled,
    variant,
    size,
  });

  const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
    ...props,
    // Defaults
    focusInputOnChange: false,
    onFocus: (e) => {
      const {onFocus} = props;

      // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
      // new quantity with one click.
      e.target.select();

      // If there is a `onFocus` property define, call it with the event captured.

      onFocus?.call(this, e);
    },
  });

  const inc = getIncrementButtonProps({
    'variant': buttonVariant,
    'aria-label': intl.formatMessage(
      {
        defaultMessage: 'Increment Quantity for {productName}',
        id: 'product_view.label.assistive_msg.quantity_increment',
      },
      {productName}
    ),
  });
  const dec = getDecrementButtonProps({
    'variant': buttonVariant,
    'aria-label': intl.formatMessage(
      {
        defaultMessage: 'Decrement Quantity for {productName}',
        id: 'product_view.label.assistive_msg.quantity_decrement',
      },
      {productName}
    ),
  });
  const input = getInputProps({
    ...(usesStepperIcons ? {width: undefined} : {maxWidth: '44px'}),
    'textAlign': 'center',
    'aria-label': intl.formatMessage({
      defaultMessage: 'Quantity',
      id: 'product_view.label.quantity',
    }),
  });

  // Accessibility improvements:
  // 1. Allow keyboard focus on the buttons - Chakra overrides values passed to get*ButtonProps()
  inc.tabIndex = '';
  dec.tabIndex = '';
  // 2. Allow Space or Enter key to trigger buttons
  // Hitting space/enter triggers a "click" event, but the component listens for "mousedown".
  // We can't reuse the buttons' onMouseDown handler, so instead we hijack the input's onKeyDown.
  // @ref https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/react%402.7.0/packages/components/number-input/src/use-number-input.ts#L333-L334
  inc.onKeyDown = (evt) => {
    if (evt.key === ' ' || evt.key === 'Enter') {
      evt.key = 'ArrowUp';
      input.onKeyDown(evt);
    }
  };
  dec.onKeyDown = (evt) => {
    if (evt.key === ' ' || evt.key === 'Enter') {
      evt.key = 'ArrowDown';
      input.onKeyDown(evt);
    }
  };

  return (
    <HStack spacing={0} {...styles.container}>
      <Button
        {...dec}
        {...styles.decrementButton}
        data-testid="quantity-decrement"
        size={usesStepperIcons ? undefined : dec.size}
      >
        {isCheckout ? (
          <CheckoutQtyMinusIcon color="inherit" />
        ) : isCartFlyout ? (
          <CartFlyoutQtyMinusIcon color="inherit" />
        ) : (
          <FormattedMessage
            defaultMessage={'\u2212'} // HTML &minus;
            id="product_view.label.quantity_decrement"
          />
        )}
      </Button>
      <Input {...input} {...styles.input} />
      <Button
        {...inc}
        {...styles.incrementButton}
        data-testid="quantity-increment"
        size={usesStepperIcons ? undefined : inc.size}
      >
        {isCheckout ? (
          <CheckoutQtyPlusIcon color="inherit" />
        ) : isCartFlyout ? (
          <CartFlyoutQtyPlusIcon color="inherit" />
        ) : (
          <FormattedMessage defaultMessage="+" id="product_view.label.quantity_increment" />
        )}
      </Button>
    </HStack>
  );
};

QuantityPicker.displayName = BaseQuantityPicker.displayName;
QuantityPicker.propTypes = {
  ...BaseQuantityPicker.propTypes,
  isDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'cartFlyout', 'checkout']),
  size: PropTypes.oneOf(['md', 'cartFlyout', 'checkout']),
};

export default QuantityPicker;
